import '.a{display:flex;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.r{gap:24px;}.ssr-plugin'
import '.v{justify-content:space-between;}.ssr-plugin'
import '.bh{padding:24px;}.ssr-plugin'
import '.b{align-items:center;}.ssr-plugin'
import '.x{margin:0 auto;}.ssr-plugin'
import '.q{cursor:pointer;}.ssr-plugin'
import '.bi{max-height:36px;}.ssr-plugin'
import '.bj{height:auto;}.ssr-plugin'
import '.bk{width:auto;}.ssr-plugin'
import '.bl{gap:8px;}.ssr-plugin'
import '.af{gap:4px;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.b9{align-items:start;}.ssr-plugin'
import '.bm{justify-content:start;}.ssr-plugin'
import '.j{gap:16px;}.ssr-plugin'
import '.s{height:1px;}.ssr-plugin'
import '.t{align-self:stretch;}.ssr-plugin'
import '.bn{background:var(--Outlines, rgba(0, 0, 0, 0.10));}.ssr-plugin'
import '.p{border:none;}.ssr-plugin'
import '.u{margin:0px;}.ssr-plugin'
import { FC, useState, useMemo, useEffect } from 'react'
import { Style, styled } from 'inlines'
import { useDependency } from '../hooks/useDependency'
import { capitalize } from '../utils/capitalize'
import { Button } from './Button'
import { Text } from './Text'
import { useTheme } from '../providers/useTheme'
import { useIsMobile } from '../hooks/useIsMobile'
import { Icon } from './Icon'
import { Link } from 'wouter'
import { useAppCtx } from '../providers/useAppCtx'
import { Language } from '@/apps/cms/components/SiteLanguages'
import useLocalStorage from '@based/use-local-storage'
import { LanguageSelector } from './LanguageSelector'
import { useContentQuery } from '../hooks/useContentQuery'
import { useTranslations } from '../hooks/useTranslations'
import { useNavigation } from '../hooks/useNavigation'

export type NavigationItem = {
  label: string
  route: string
}

export type NavigationProps = {
  as?: 'nav' | 'footer'
  isExpandable?: boolean
  isCartVisible?: boolean
  cartItems?: number
  languages: Language[]
  style?: Style
  'aria-label'?: string
}

type ItemsProps = {
  as: 'nav' | 'footer'
  isMobile: boolean
  items: NavigationItem[]
  colors: ReturnType<typeof useTheme>['colors']
  votesCount?: number
  isCartVisible?: boolean
}

const Items: FC<ItemsProps> = ({
  as,
  isMobile,
  items,
  colors,
  votesCount,
  isCartVisible,
}) => {
  return (
    <div className="a h"
      style={{
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'start' : 'center',
        justifyContent: isMobile ? 'start' : 'center',
        gap: isMobile ? 16 : 24,}}
    >
      {items?.map((item, index) => {
        if (item.route === '/cart' && !isCartVisible) {
          return null
        }

        if (!item.label) {
          return null
        }

        return (
          <Link
            id={`navigation-link-${item.label.toLowerCase()}`}
            key={`${item.label}-${item.route}-${index}`}
            href={item.route}
            style={{ textDecoration: 'none' }}
          >
            <Text
              variant={
                as === 'footer'
                  ? 'subheading-medium'
                  : isMobile
                    ? 'subtitle'
                    : 'subheading-medium'
              }
              color={colors.textPrimary}
            >
              {item.label}{' '}
              {item.route === '/cart' && votesCount
                ? ` (${String(votesCount)})`
                : null}
            </Text>
          </Link>
        )
      })}
    </div>
  )
}

export const Navigation: FC<NavigationProps> = ({
  as = 'nav',
  isExpandable = false,
  isCartVisible = false,
  cartItems = 0,
  'aria-label': ariaLabelProp,
  languages,
  style,
}) => {
  const isMobile = useIsMobile()
  const { colors } = useTheme()
  const [isExpanded, setIsExpanded] = useState(false)
  const { votesCount } = useAppCtx()
  const [storageActiveLanguage, setStorageActiveLanguage] =
    useLocalStorage('language')
  const navigation = useNavigation()
  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev)
  }

  const { data: ed = {} } = useContentQuery('edition')

  const { data: dictionary = {} } = useTranslations('navigation')

  const logo = ed?.htmlConfig?.logo?.src

  const navLabel =
    ariaLabelProp ?? (as === 'footer' ? 'Footer navigation' : 'Main navigation')

  const menuId =
    as === 'footer' ? 'navigation-menu-footer' : 'navigation-menu-nav'

  const showMobileMenu = isMobile && (!isExpandable || isExpanded)

  const defaultLanguage = useMemo(
    () => languages?.filter((language) => Boolean(language.default))[0]?.value,
    [useDependency(languages)],
  )

  useEffect(() => {
    if (!storageActiveLanguage) {
      setStorageActiveLanguage(defaultLanguage)
    }
  }, [defaultLanguage])

  return (
    <styled.nav className="a i r v bh h"
      {...(as === 'nav' ? { 'aria-label': navLabel } : {})}
      style={{
        borderBottom: as !== 'footer' ? `1px solid ${colors.border}` : 'none',
        backdropFilter: `blur(24px)`,
        background: colors.background,
        '&:before': {
          content: "' '",
          position: 'absolute',
          inset: 0,
          opacity: 0.05,
          background: as === 'footer' ? colors.textPrimary : 'transparent',
          pointerEvents: 'none',
        },
        ...style,
      }}
    >
      <div className="a b v r h x"
        style={{
          maxWidth: !isMobile ? 1280 : undefined,}}
      >
        {logo && as === 'nav' ? (
          <Link href="/" style={{ textDecoration: 'none' }}>
            <img className="q bi bj bk"
              src={logo}
              style={{
                maxWidth: isMobile ? 150 : 192,}}
            />
          </Link>
        ) : (
          <Link href="/" style={{ textDecoration: 'none' }}>
            <Text
              isNotSelectable
              isAlwaysVisible
              variant="display-regular"
              color={colors.textSecondary}
              style={{ fontSize: 16 }}
            >
              {capitalize(dictionary?.contest_title)}
            </Text>
          </Link>
        )}

        {!isMobile && as === 'footer' && (
          <nav className="a v b"
            aria-label={navLabel}
          >
            <Items
              as={as}
              isMobile={isMobile}
              items={navigation}
              colors={colors}
              votesCount={votesCount}
              isCartVisible={isCartVisible}
            />
          </nav>
        )}

        {!isMobile && as !== 'footer' && (
          <LanguageSelector
            value={storageActiveLanguage}
            defaultValue={defaultLanguage}
            languages={languages}
            style={{ marginLeft: 'auto' }}
          />
        )}

        <div className="a bl b">
          {isCartVisible && (
            <Link href="/cart">
              <Button
                id="navigation-link-cart"
                isRaw
                color={colors.textSecondary}
                style={{ padding: 6 }}
              >
                <span className="a af b c"
                >
                  <Icon variant="bag" color={colors.textSecondary} />
                  <Text
                    isNotSelectable
                    color={colors.textSecondary}
                    style={{ fontSize: 16 }}
                  >
                    {String(cartItems)}
                  </Text>
                </span>
              </Button>
            </Link>
          )}

          {isMobile && isExpandable && (
            <Button
              id="navigation-open-menu"
              isRaw
              on={{ click: handleToggleExpand }}
              aria-label={capitalize(
                (isExpanded
                  ? dictionary?.close_menu_button
                  : dictionary?.open_menu_button) ?? '',
              )}
              aria-expanded={isExpanded}
              aria-controls={menuId}
              style={{ padding: 6 }}
            >
              <span
                style={{
                  transform: isExpanded ? 'rotate(270deg)' : 'rotate(90deg)',
                }}
              >
                <Icon
                  variant="chevron"
                  color={colors.textSecondary}
                  aria-hidden={true}
                />
              </span>
            </Button>
          )}

          {!isMobile && as === 'footer' && (
            <LanguageSelector
              value={storageActiveLanguage}
              defaultValue={defaultLanguage}
              languages={languages}
            />
          )}
        </div>
      </div>

      {showMobileMenu && (
        <div className="a i b9 bm j h"
          id={menuId}
          {...(as === 'footer' ? { 'aria-label': navLabel } : {})}
        >
          <Items
            as={as}
            isMobile={isMobile}
            items={navigation}
            colors={colors}
            votesCount={votesCount}
            isCartVisible={isCartVisible}
          />

          <hr className="s t bn p u"
          />

          <LanguageSelector
            value={storageActiveLanguage}
            defaultValue={defaultLanguage}
            languages={languages}
          />
        </div>
      )}
    </styled.nav>
  )
}
